import { useEffect, useState } from 'react';
import {
  decodeUrlEntities,
  extractGid,
  extractSheetId,
  generateSheetPdfExportUrl,
  getEmbedUrl,
} from './utils';
import { ReportState } from './types';

interface UseReportManagerParams {
  reportUrl: string;
  onLoad?: () => void;
  isGoogleSheet: boolean;
}

interface UseReportManagerReturn extends ReportState {
  handleRefresh: () => void;
  tryPdfFallback: () => boolean;
  handleIframeLoad: () => void;
  handleIframeError: () => void;
  baseUrl: string;
  embedUrl: string;
}

export const useReportManager = ({
  reportUrl,
  onLoad,
  isGoogleSheet,
}: UseReportManagerParams): UseReportManagerReturn => {
  const decodedReportUrl = decodeUrlEntities(reportUrl.trim());
  const embedUrl = getEmbedUrl(decodedReportUrl);

  const [reportLoaded, setReportLoaded] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string>(embedUrl || decodedReportUrl || '');
  const [triedPdfFallback, setTriedPdfFallback] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  useEffect(() => {
    setIframeSrc(embedUrl || decodedReportUrl || '');
    setReportLoaded(false);
    setTriedPdfFallback(false);
    setIframeError(null);
  }, [embedUrl, decodedReportUrl]);

  const handleRefresh = () => {
    setReportLoaded(false);
    const base = embedUrl || decodedReportUrl || '';
    if (!base) return;

    setIframeError(null);
    setTriedPdfFallback(false);
    const separator = base.includes('?') ? '&' : '?';
    const refreshedUrl = `${base}${separator}_t=${Date.now()}`;

    setIframeSrc(refreshedUrl);
  };

  const tryPdfFallback = (): boolean => {
    if (!isGoogleSheet) return false;
    if (triedPdfFallback) return false;

    const sheetId = extractSheetId(decodedReportUrl) || extractSheetId(embedUrl);
    if (!sheetId) return false;

    const gid = extractGid(decodedReportUrl + embedUrl);
    const pdfUrl = generateSheetPdfExportUrl(embedUrl, sheetId, gid);

    setTriedPdfFallback(true);
    setIframeSrc(pdfUrl);
    setReportLoaded(false);

    return true;
  };

  const handleIframeLoad = () => {
    console.log('LookerReport iframe loaded src:', iframeSrc);
    setIframeError(null);
    setReportLoaded(true);
    if (onLoad) onLoad();
  };

  const handleIframeError = () => {
    const tried = tryPdfFallback();
    if (!tried) {
      const errorMessage =
        'Unable to load document — it may be private, not published, or blocked from embedding.';
      console.error('LookerReport iframe error loading:', iframeSrc, errorMessage);
      setIframeError(errorMessage);
      setReportLoaded(false);
    }
  };

  return {
    iframeSrc,
    reportLoaded,
    triedPdfFallback,
    iframeError,
    handleRefresh,
    tryPdfFallback,
    handleIframeLoad,
    handleIframeError,
    baseUrl: decodedReportUrl,
    embedUrl,
  };
};
